import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { violationRecordsService, employeesService, notificationsService } from '../../services/supabaseService';

const PointCreditSystem = () => {
  const [creditLog, setCreditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Settings', path: '/settings' },
    { label: 'Point Credit System', path: '/settings/point-credits' }
  ];

  useEffect(() => {
    loadCreditLog();
    // Check if we should auto-run on mount (once per day)
    const lastRunDate = localStorage.getItem('lastPointCreditRun');
    const today = new Date()?.toISOString()?.split('T')?.[0];
    
    if (lastRunDate !== today) {
      // Auto-run on first visit of the day
      handleApplyCredits();
    } else {
      setLastRun(lastRunDate);
    }
  }, []);

  const loadCreditLog = async () => {
    try {
      setLoading(true);
      const log = await violationRecordsService?.getPointCreditLog();
      setCreditLog(log || []);
    } catch (error) {
      console.error('Error loading credit log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCredits = async () => {
    if (processing) return;
    
    try {
      setProcessing(true);
      const results = await violationRecordsService?.applyAutomaticPointCredits();
      
      if (results && results?.length > 0) {
        // Get updated employee data for notifications
        const updatedEmployees = await employeesService?.getAll();
        
        // Create real-time notifications for each point credit applied
        for (const result of results) {
          const employee = updatedEmployees?.find(e => e?.id === result?.employeeId);
          if (employee) {
            await notificationsService?.createPointCreditNotification({
              employeeId: result?.employeeId,
              employeeName: result?.employeeName,
              employeeDepartment: employee?.department,
              pointsCredited: parseFloat(result?.pointsCredited || 1.0),
              pointsBefore: parseFloat(result?.pointsBefore),
              pointsAfter: parseFloat(result?.pointsAfter),
              daysWithoutViolation: result?.daysWithoutViolation,
              complianceScore: employee?.complianceScore || 100,
              complianceStatus: employee?.complianceStatus || 'Good'
            });
          }
        }
        
        alert(`Successfully applied credits to ${results?.length} employee(s)!\n\n${results?.map(r => `${r?.employeeName}: ${r?.pointsBefore} → ${r?.pointsAfter} points (${r?.daysWithoutViolation} days clean)`)?.join('\n')}`);
        await loadCreditLog();
      } else {
        alert('No employees are currently eligible for point credits.\n\nEmployees must:\n- Have current points > 0\n- Be Active status\n- Have 30+ days without violations');
      }
      
      // Store last run date
      const today = new Date()?.toISOString()?.split('T')?.[0];
      localStorage.setItem('lastPointCreditRun', today);
      setLastRun(today);
    } catch (error) {
      console.error('Error applying credits:', error);
      alert('Failed to apply point credits. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="flex items-center justify-between mb-6 mt-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Point Credit System</h1>
              <p className="text-muted-foreground">Automatic 1-point credit after 30 days without violations</p>
            </div>
            <Button
              onClick={handleApplyCredits}
              disabled={processing}
              className="flex items-center gap-2"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Award" size={18} />
                  Apply Credits Now
                </>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Icon name="Calendar" size={24} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Run</p>
                  <p className="text-xl font-bold text-foreground">
                    {lastRun ? new Date(lastRun)?.toLocaleDateString() : 'Never'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Icon name="TrendingDown" size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Credits Applied</p>
                  <p className="text-xl font-bold text-foreground">{creditLog?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Icon name="Award" size={24} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Points Returned</p>
                  <p className="text-xl font-bold text-foreground">
                    {creditLog?.reduce((sum, log) => sum + parseFloat(log?.pointsCredited || 0), 0)?.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Credit History</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-3"></div>
                  <p className="text-muted-foreground">Loading credit history...</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Employee</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Points Before</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Points After</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Credit</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Days Clean</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {creditLog?.map((log) => (
                      <tr key={log?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{log?.employeeName}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{log?.pointsBefore}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-green-600">{log?.pointsAfter}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            -{log?.pointsCredited}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">{log?.daysWithoutViolation} days</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(log?.creditedAt)?.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{log?.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {creditLog?.length === 0 && (
                  <div className="text-center py-12">
                    <Icon name="Award" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No point credits have been applied yet</p>
                    <p className="text-sm text-muted-foreground mt-2">Click "Apply Credits Now" to check for eligible employees</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">How Point Credits Work</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Employees receive 1 point credit after 30 consecutive days without violations</li>
                  <li>• Credits are automatically applied when you click "Apply Credits Now"</li>
                  <li>• Only active employees with current points &gt; 0 are eligible</li>
                  <li>• The system runs automatically once per day on first visit</li>
                  <li>• All credits are logged in the history table below</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointCreditSystem;